import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ReloadOutlined, RocketOutlined } from "@ant-design/icons";
import { Button, theme } from "antd";
import { observer } from "mobx-react";
import { ESubscriptionCancellationReason } from "@devowl-wp/api-real-commerce";
import { useStores } from "../../../../../../store/stores.js";
import { COLOR_ERROR_SOFT, COLOR_ERROR_SOFT_DEEP, COLOR_PRIMARY_DEEP } from "../../../../../../style/antdTheme.js";
import { LOCAL_STORAGE_CHECKOUT_RESTORE_VALUES } from "../../../../../../utils/checkoutRestoreFormStorage.js";
import { __, _i } from "../../../../../../utils/i18n.js";
const ExpiredSubscriptionCard = observer(({ subscription })=>{
    const { token } = theme.useToken();
    const { accountV2Store } = useStores();
    const { id: productPlanId, product } = subscription.data.productPlan;
    const productName = product.name;
    // Merge identity into the checkout's restore bag (preserves coupon /
    // address). Skip bumping `updated` so the coupon's TTL stays intact.
    const prefillCheckoutFormValues = ()=>{
        const user = accountV2Store.session?.user;
        if (!user || typeof localStorage === "undefined") {
            return;
        }
        try {
            const raw = localStorage.getItem(LOCAL_STORAGE_CHECKOUT_RESTORE_VALUES);
            const existing = raw ? JSON.parse(raw) : {};
            const next = {
                ...existing,
                firstName: user.firstName ?? "",
                lastName: user.lastName ?? "",
                email: user.email ?? ""
            };
            localStorage.setItem(LOCAL_STORAGE_CHECKOUT_RESTORE_VALUES, JSON.stringify(next));
        } catch  {
        // Quota / corrupted JSON / disabled storage — the checkout just
        // starts with an empty form, which is the pre-prefill behaviour.
        }
    };
    // CTA link resolution:
    //  1. If the account shortcode was rendered with `checkout="<page-id>"`,
    //     the boot wires the resolved permalink onto
    //     `window.realCommerceCheckout.checkoutUrl`. We deep-link to it with
    //     `?product=<id>&plan=<planId>` so Checkout V2 lands on the exact
    //     same product+plan the customer had — zero re-selection.
    //  2. Otherwise we fall back to the product's public marketing page so
    //     the CTA still works on sites that haven't configured the shortcode
    //     attribute. The customer re-picks the plan there.
    const checkoutUrl = window.realCommerceCheckout?.checkoutUrl;
    const buildHref = ()=>{
        if (checkoutUrl) {
            // `checkoutUrl` is either an absolute permalink (PHP
            // `get_permalink()` returns one) or a site-relative path; the
            // second `URL()` argument handles the relative case without
            // double-prefixing an absolute one.
            const url = new URL(checkoutUrl, window.location.origin);
            url.searchParams.set("product", String(product.id));
            url.searchParams.set("plan", String(productPlanId));
            return url.toString();
        }
        return product.productDescriptionPage || undefined;
    };
    const href = buildHref();
    const isPaymentFailed = subscription.data.cancellationReason === ESubscriptionCancellationReason.PaymentFailed;
    // Two scenarios produce different emotional contexts:
    //  - Voluntary cancellation -> calm comeback; brand cyan gradient + rocket
    //    icon, same visual language as the Upgrade promo banner.
    //  - Payment-failed -> warm red-soft gradient + reload icon; signals
    //    "needs attention" without alarm. SubscriptionList auto-expands the
    //    card so this CTA is one click away from page load.
    const gradient = isPaymentFailed ? `linear-gradient(120deg, ${COLOR_ERROR_SOFT} 0%, ${COLOR_ERROR_SOFT_DEEP} 50%, ${COLOR_ERROR_SOFT} 100%)` : `linear-gradient(120deg, ${token.colorPrimary} 0%, ${COLOR_PRIMARY_DEEP} 50%, ${token.colorPrimary} 100%)`;
    const Icon = isPaymentFailed ? ReloadOutlined : RocketOutlined;
    const title = isPaymentFailed ? __("Restore your %s subscription!", productName) : __("Resubscribe to %s!", productName);
    const body = isPaymentFailed ? _i(__("Your subscription has been {{strong}}cancelled{{/strong}} because we couldn't collect the renewal payment after several reminders. You can pick up your subscription again any time - it only takes a minute."), {
        strong: /*#__PURE__*/ _jsx("strong", {})
    }) : __("Continue right where you left off in under a minute.");
    const ctaLabel = __("Restore subscription");
    const variantClass = isPaymentFailed ? "rc-account-v2-expired-hero--payment-failed" : "rc-account-v2-expired-hero--voluntary";
    const ctaTextColor = isPaymentFailed ? COLOR_ERROR_SOFT : token.colorPrimary;
    return /*#__PURE__*/ _jsxs("div", {
        className: `rc-account-v2-expired-hero ${variantClass}`,
        style: {
            background: gradient
        },
        children: [
            /*#__PURE__*/ _jsx(Icon, {
                className: "rc-account-v2-expired-hero__icon",
                "aria-hidden": true
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "rc-account-v2-expired-hero__text",
                children: [
                    /*#__PURE__*/ _jsx("strong", {
                        className: "rc-account-v2-expired-hero__title",
                        children: title
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "rc-account-v2-expired-hero__body",
                        children: body
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Button, {
                size: "large",
                className: "rc-account-v2-expired-hero__cta",
                disabled: !href,
                href: href,
                target: "_blank",
                rel: "noopener noreferrer",
                onClick: prefillCheckoutFormValues,
                style: {
                    color: ctaTextColor
                },
                children: ctaLabel
            })
        ]
    });
});
export { ExpiredSubscriptionCard };
